/* eslint-disable no-underscore-dangle */
import {
  Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { UntypedFormGroup, AbstractControl, UntypedFormBuilder } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { isCustomDimensionsValue } from 'app/utils/is-custom-dimensions-value';
import { FormsFieldsState, ModifierTypes, SelectDropdownPositionMod, SvgIconSizeMod } from 'models/layout-mod.model';
import { BaseComponent } from 'shared/base/base.component';
import { StylingService } from 'shared/services/styling.service';

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent<T> extends BaseComponent implements OnInit, OnChanges {
  @ViewChild('select') select!: NgSelectComponent;

  @ViewChild('search') searchElement!: ElementRef;

  @Output() onSearch = new EventEmitter<string>();

  @Output() onAdd = new EventEmitter<any>();

  @Output() onRemove = new EventEmitter();

  @Output() onClear = new EventEmitter<string>();

  @Output() onClose = new EventEmitter<any>();

  @Output() onOpen = new EventEmitter<any>();

  @Output() onSearchKeydown = new EventEmitter<any>();

  @Output() onCloseCustomMessage = new EventEmitter();

  @Input() options!: T[];

  @Input() defaultValue!: any;

  @Input() mainForm!: UntypedFormGroup;

  @Input() controlName!: string;

  @Input() labelKey!: string;

  @Input() valueKey = '';

  @Input() disabledKey!: string;

  @Input() searchable?: boolean = false;

  @Input() multiple?: boolean = false;

  @Input() clearable?: boolean = false;

  @Input() customClearIcon?: boolean = false;

  @Input() clearOnBackspace?: boolean = false;

  @Input() placeholder?: string = '';

  @Input() placeholderSearch?: string = '';

  @Input() clearAllLabel?: string = '';

  @Input() maxSelectedItems?: number;

  @Input() allowDisabledSelection = false;

  @Input() hideIncompatibleValues = false;

  @Input() focusOnInputSearch = false;

  @Input() suffix?: string = '';

  @Input() prefix?: string = '';

  @Input() invalidValueText?: string = '';

  /**
   * All options are rendered by default.
   * If set to false, only selected will be rendered, to speed up the page load.
   * Mostly needed for configuration page, since it contains hundreds of dropdowns.
   */
  @Input() renderOptionsOnInit = true;

  @Input() highlight = false;

  @Input() state?: FormsFieldsState;

  @Input() layoutMod: ModifierTypes[] = [];

  @Input() dropdownPosition: SelectDropdownPositionMod = 'bottom';

  layoutModifier = '';

  _controlNameInputValue = '';

  _invalidValueToShow = '';

  get invalidValueToShow(): string {
    return this._invalidValueToShow;
  }

  @Input() set invalidValueToShow(value: string) {
    if (value) {
      this._invalidValueToShow = value.toString();
    } else {
      this._invalidValueToShow = '';
    }
  }

  get controlNameInputValue(): string {
    return this._controlNameInputValue;
  }

  @Input() set controlNameInputValue(value: string) {
    if (value) {
      const val = value.toString();
      const controlValue = (this.mainForm.controls[this.controlName].value ? this.mainForm.controls[this.controlName].value.toString() : '');
      if (
        controlValue !== val && !isCustomDimensionsValue(val)
      ) {
        this._controlNameInputValue = val;
      } else {
        this._controlNameInputValue = controlValue;
      }
    } else {
      this._controlNameInputValue = '';
    }
  }

  searchForm!: UntypedFormGroup;

  control!: AbstractControl | null;

  _options: T[] = [];

  constructor(
    protected fb: UntypedFormBuilder,
    private stylingService: StylingService
  ) { super(); }

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.renderOptionsOnInit) {
      this._options = this.options;
    }

    if (changes && changes.highlight && changes.highlight.currentValue === true) {
      if (this._options.length !== this.options.length) {
        this._options = this.options;
      }
    }
  }

  private init(): void {
    this.control = this.mainForm.controls[this.controlName];
    this.layoutModifier = this.stylingService.concatLayoutModifiers('dropdown', this.layoutMod);

    if (this.defaultValue && this.control && !this.control.value) {
      this.control.setValue(this.defaultValue, {
        emitEvent: false,
        onlySelf: true
      });
    }
    if (this.renderOptionsOnInit) {
      this._options = this.options;
    } else {
      const controlValue = (this.control && this.control.value) || '';
      if (controlValue) {
        const selectedOption = this.options.find((o: any) => (this.valueKey ? o[this.valueKey] : o) === controlValue);

        if (selectedOption) {
          this._options = [selectedOption];
        }
      }
    }

    this.setupSearchForm();
  }

  setupSearchForm(): void {
    if (!this.placeholderSearch) return;

    this.searchForm = this.fb.group({
      search: ['']
    });

    this.searchForm.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        if (this.onSearch.observers.length > 0) {
          this.onSearch.emit(this.searchForm.controls.search.value);
        } else {
          this.onSearchDefault();
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onAddEvent(event: any): void {
    this.onAdd.emit(event);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onRemoveEvent(event: any): void {
    this.onRemove.emit(event);
    this.select.close();
  }

  onOpenEvent(): void {
    if (this._options.length !== this.options.length) {
      this._options = this.options;
    }

    if (this.searchForm) {
      if (this.focusOnInputSearch) {
        setTimeout(() => {
          this.searchElement.nativeElement.focus();
        });
      }
      if (this.searchForm.controls.search.value !== '') {
        this.searchForm.controls.search.patchValue('');
      }
    }

    this.onOpen.emit(this.controlName);
  }

  onClearEvent(): void {
    if (!this.mainForm || this.mainForm.status === 'DISABLED') {
      return;
    }

    this.onClear.emit(this.controlName);
  }

  onCloseEvent(): void {
    if (this.searchForm && this.searchForm.controls.search.value) {
      const highlightedOption = this.highlightedOption();
      if (highlightedOption) {
        this.onClose.emit(highlightedOption.value);
      } else {
        this.onClose.emit(this.searchForm.controls.search.value);
      }
    } else {
      this.onClose.emit((this.control && this.control.value) || '');
    }
  }

  highlightedOption(): any | undefined {
    const markedOption = this.select.dropdownPanel.contentElementRef.nativeElement.querySelector('.ng-option-marked');

    if (markedOption) {
      const index = Array.from(markedOption.parentNode.children).indexOf(markedOption);

      return this._options[index];
    }

    return undefined;
  }

  searchOnEnter(): void {
    if (this.searchForm) {
      this.onClose.emit(this.searchForm.controls.search.value);
      this.select.close();
    }
  }

  searchOnKeydown(event: Event): void {
    this.onSearchKeydown.emit(event);
  }

  onSearchDefault(): void {
    let searchValue = this.searchForm.controls.search.value.trim();
    if (searchValue !== '') {
      searchValue = searchValue.toLowerCase();
      this._options = this.options.filter((o: any) => (this.labelKey && o[this.labelKey].toLowerCase()
        .indexOf(searchValue) > -1)
        || (!this.labelKey && typeof o === 'string' && o.toLowerCase()
          .indexOf(searchValue) > -1));
    } else {
      this._options = this.options;
    }
  }

  onClosePopover(event: Event): void {
    this.onCloseCustomMessage.emit(event);
  }

  protected readonly svgIconSizeMod = SvgIconSizeMod;
}
